import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '@components/header'
import { columnDebtorsSalesPerson } from '@utils/columns'
import Layout from '@components/Layout'
import { useNavigate } from 'react-router-dom'
import { debouncedSet } from '@utils/debounce'
import { useQuery } from '@tanstack/react-query'
import { fetchPaymentDistribution, fetchCashAccounts } from '@utils/fetchData'
import { getToday } from '@utils/time'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'
import DebtsOneSlpModal from '@components/Modal/DebtSalesPersonModal/DebtsOneSlp'
import { useSelector } from 'react-redux'
import { downloadExcel } from '@utils/downloadPdf'
import moment from 'moment'
import { Select } from 'antd'

const PaymentDistribution = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const formattedDate = getToday()

	const [slpName, setSlpName] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [cashAcc, setCashAcc] = useState('')
	const [pageSize] = useState(50)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)

	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'paymentDistribution',
			startDate,
			endDate,
			cashAcc,
			pageSize,
			page,
		],
		queryFn: fetchPaymentDistribution,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: cashAccs = [] } = useQuery({
		queryKey: ['cashAccounts'],
		queryFn: fetchCashAccounts,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/excelgenerators/export-debt-by-managers',
			params: {
				startDate,
				endDate,
				pageSize: 1000,
				pageToken: 0,
			},
			fileName: "Qazrdorlar menejerlar bo'yicha",
			onLoadingChange: setLoadingExcel,
		})
	}

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prevPage) => prevPage - 1)
		}
	}

	const columns = [
		{
			title: t('docNum'),
			dataIndex: 'ID документа',
			key: 'ID документа',
		},
		{
			title: t('paymentDate'),
			dataIndex: 'Дата платежа',
			key: 'Дата платежа',
			render: (text) => {
				return `${moment(text).format('DD.MM.YYYY')}`
			},
		},

		{
			title: t('docType'),
			dataIndex: 'Тип документа',
			key: 'Тип документа',
		},
		{
			title: t('paymentNum'),
			dataIndex: 'Номер платежа',
			key: 'Номер платежа',
		},
		{
			title: t('cardName'),
			dataIndex: 'Клиент',
			key: 'Клиент',
		},
		{
			title: t('cashAcc'),
			dataIndex: 'Номер Счета',
			key: 'Номер Счета',
		},
		{
			title: t('docTotal'),
			dataIndex: 'Полная сумма документа',
			key: 'Полная сумма документа',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},

		{
			title: t('Покрытая себест.'),
			dataIndex: 'Покрытая себест.',
			key: 'Покрытая себест.',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('Полная себест. (Прайс 2)'),
			dataIndex: 'Полная себест. (Прайс 2)',
			key: 'Полная себест. (Прайс 2)',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('paymentSum'),
			dataIndex: 'Сумма платежа',
			key: 'Сумма платежа',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('Доля оплаты'),
			dataIndex: 'Доля оплаты',
			key: 'Доля оплаты',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('Доступные средства'),
			dataIndex: 'Доступные средства',
			key: 'Доступные средства',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
	]

	return (
		<Layout>
			<Header
				title={'paymentDistribution'}
				hasMoreData={data.length === pageSize}
				columns={columns}
				fdata={data}
				scrollX={1500}
				loading={isLoading}
				renderFilters={() => {
					return (
						<div className="mt-4 flex flex-col gap-3 sm:mt-0 w-full">
							<p className="font-nunito">{t('cashAcc')}</p>
							<Select
								value={cashAcc}
								className="flex w-[300px] h-8   items-center justify-center "
								style={{ color: 'black' }}
								onChange={(val) => {
									setCashAcc(val ?? '')
								}}
								filterOption={(input, option) =>
									(option?.label ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								showSearch
								allowClear
								options={cashAccs.map((cash) => {
									return {
										label: `${cash.acctCode} - ${cash.acctName}`,
										value: cash.acctCode,
									}
								})}
							/>
						</div>
					)
				}}
				currentPage={page + 1}
				onStartDateChange={handleStartDate}
				onEndDateChange={handleEndDate}
				startDate={startDate}
				endDate={endDate}
				formattedDate={formattedDate}
				// isShowExcelBtn
				// handleDownloadExcel={handleDownloadExcel}
				// loadingExcel={loadingExcel}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
			/>
			<DebtsOneSlpModal
				visible={viewModalOpen}
				onClose={() => setViewModalOpen(false)}
				data={selectedRowData}
			/>
		</Layout>
	)
}

export default PaymentDistribution
